import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { Button, Modal, Form } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { gatePassApi, warehouseApi } from 'api';
import { useUserContext } from 'providers/UserProvider';
import {
  GATEPASS_TYPE as GatePassType,
  GATEPASS_TYPE,
} from 'constants/gatepass';

const GatePassCreateModal = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { selectedTenant, selectedWarehouse } = useUserContext();
  const tenant = selectedTenant?.domains?.[0]?.domain;
  const warehouseDomain = selectedWarehouse?.domain;
  const navigate = useNavigate();

  const [type, setType] = useState(GatePassType.STOCK_TRANSFER);
  const [partyId, setPartyId] = useState('');
  const [referenceNo, setReferenceNo] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [warehouses, setWarehouses] = useState([]);
  const [loadingWarehouses, setLoadingWarehouses] = useState(false);

  const isPartySelectionEnabled =
    type === GATEPASS_TYPE.RETURN_TO_VENDOR ||
    type === GATEPASS_TYPE.STOCK_TRANSFER;

  useEffect(() => {
    const fetchWarehouses = async () => {
      setLoadingWarehouses(true);
      try {
        const response = await warehouseApi.list();
        setWarehouses(response.results || []);
      } catch (error) {
        toast.error('Failed to fetch warehouses.');
      } finally {
        setLoadingWarehouses(false);
      }
    };

    if (isOpen && type === GATEPASS_TYPE.STOCK_TRANSFER) {
      fetchWarehouses();
    }
  }, [isOpen, type]);

  const handleCreate = async () => {
    if (!warehouseDomain || !type) return;

    const params = {
      gate_pass_type: type,
      reference_no: referenceNo || undefined,
    };

    if (type === GatePassType.RETURN_TO_VENDOR) {
      params.vendor = partyId;
    } else if (type === GatePassType.STOCK_TRANSFER) {
      params.target_warehouse = partyId;
    }

    setIsLoading(true);
    try {
      const newGatePass = await gatePassApi.create(params);
      toast.success('GatePass created successfully!');
      closeModal();
      navigate(`/${tenant}/${warehouseDomain}/gate-passes/${newGatePass.id}`);
    } catch (error) {
      toast.error('Failed to create GatePass.');
    } finally {
      setIsLoading(false);
    }
  };

  const openModal = () => setIsOpen(true);
  const closeModal = () => {
    setIsOpen(false);
    setPartyId('');
    setReferenceNo('');
    setWarehouses([]);
  };

  return (
    <>
      <div onClick={openModal}>{children}</div>
      <Modal show={isOpen} onHide={closeModal}>
        <Modal.Header closeButton>
          <Modal.Title>Create GatePass</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted">Stock Transfer is only available.</p>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Type</Form.Label>
              <Form.Select value={type} onChange={e => setType(e.target.value)}>
                {Object.values(GatePassType).map(gpType => (
                  <option key={gpType} value={gpType}>
                    {gpType.replace(/_/g, ' ').toUpperCase()}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>
                {type === GatePassType.STOCK_TRANSFER
                  ? 'Target Warehouse'
                  : 'Party ID'}
              </Form.Label>
              {type === GatePassType.STOCK_TRANSFER ? (
                <Form.Select
                  value={partyId}
                  onChange={e => setPartyId(e.target.value)}
                  disabled={!isPartySelectionEnabled || loadingWarehouses}
                >
                  <option value="">Select a warehouse</option>
                  {warehouses.map(wh => (
                    <option key={wh.id} value={wh.id}>
                      {wh.name}
                    </option>
                  ))}
                </Form.Select>
              ) : (
                <Form.Control
                  type="number"
                  value={partyId}
                  onChange={e => setPartyId(parseInt(e.target.value, 10) || '')}
                  disabled={!isPartySelectionEnabled}
                  placeholder={
                    !isPartySelectionEnabled
                      ? 'Select a type first'
                      : 'Enter Vendor/Warehouse ID'
                  }
                />
              )}
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Reference No.</Form.Label>
              <Form.Control
                type="text"
                value={referenceNo}
                onChange={e => setReferenceNo(e.target.value)}
                placeholder="(Optional)"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeModal}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleCreate}
            disabled={
              isLoading || type !== GatePassType.STOCK_TRANSFER || !partyId
            }
          >
            {isLoading
              ? 'Creating...'
              : type === GatePassType.STOCK_TRANSFER
              ? 'Create and View'
              : 'Unavailable for this type'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

GatePassCreateModal.propTypes = {
  children: PropTypes.node.isRequired,
};

export default GatePassCreateModal;
